// src/prototype/data/adapters/useCaseContentAdapter.ts
// ============================================================
// USE-CASE CONTENT ADAPTER — PUBLIC API
//
// The ONLY module the v4 hub tabs import for content. It reads BigQuery-shaped rows
// (getContentRows — mock today, a live fetch later) and maps them to the view models
// the components render. Swapping the row source never touches the UI.
//
// A dimension's score aggregates its measures (mean of measure_results.raw_score); the
// composite comes straight from client_use_case_scores.total_score. Rationale text is
// joined from client_measure_rationalizations by measure_id.
// ============================================================

import type { DecisionBrief } from '../decisionBrief';
import { getContentRows } from './useCaseContentMock';
import {
  DIM_META,
  SCORES_AS_OF,
  SCORING_RUN,
  type DimensionId,
  type ScoresVM,
  type DimensionVM,
  type MeasureVM,
  type RiskVM,
  type MilestoneVM,
  type RequirementVM,
  type AlignmentVM,
  type RoiSummaryVM,
} from './useCaseContentTypes';

const round1 = (v: number) => Math.round(v * 10) / 10;

export function getScores(brief: DecisionBrief): ScoresVM {
  const rows = getContentRows(brief);
  const ratById = new Map(rows.rationalizations.map((r) => [r.measure_id, r]));

  const dims: DimensionVM[] = DIM_META.map((meta) => {
    const measureRows = rows.measures.filter((m) => m.dimension === meta.id);
    const measures: MeasureVM[] = measureRows.map((m) => {
      const rat = ratById.get(m.measure_id);
      return {
        id: m.measure_id,
        name: m.measure_name,
        raw: m.raw_score,
        evaluated: m.evaluated ?? true,
        scale: m.score_scale ?? '0to100',
        conf: m.confidence_level,
        rat: rat?.rationalization_text ?? '',
        ev: m.evidence_json.map((e) => ({ type: e.type, src: e.src, excerpt: e.excerpt })),
        weight: rat?.weight_label,
        source: rat?.source_type,
      };
    });
    // A dimension's bar is the authored display score when provided (featured drilldown —
    // illustrative 4-dim breakdown), else it aggregates its measures.
    //
    // The rollup counts EVALUATED measures only. An unevaluated measure has no score to average,
    // and substituting one (a zero, or the warehouse value behind rejected reasoning) would move
    // the dimension bar on the strength of something the page openly says it does not have.
    const scored = measures.filter((m) => m.evaluated && m.raw !== null);
    const authored = rows.dimScores?.[meta.id as DimensionId];
    const score =
      authored !== undefined
        ? round1(authored)
        : scored.length
          ? round1(scored.reduce((s, m) => s + (m.raw as number), 0) / scored.length)
          : 0;
    return {
      id: meta.id as DimensionId,
      score,
      measures,
      coverage: { evaluated: scored.length, total: measures.length },
      rollup: authored !== undefined ? 'authored' : 'measures',
    };
  });

  return {
    total: round1(rows.score.total_score),
    confidence: rows.score.confidence_level,
    compositeWeights: rows.compositeWeights,
    asOf: SCORES_AS_OF,
    run: SCORING_RUN,
    dims,
    coverage: {
      evaluated: dims.reduce((a, d) => a + d.coverage.evaluated, 0),
      total: dims.reduce((a, d) => a + d.coverage.total, 0),
    },
    provenance: rows.provenance,
    personalizationDelta: rows.personalizationDelta,
    priorityThemes: rows.priorityThemes,
  };
}

export function getRoiSummary(brief: DecisionBrief): RoiSummaryVM {
  const { roiOutputs: o } = getContentRows(brief);
  return {
    outputs: {
      annualBenefit: o.annual_benefit_display,
      annualBenefitBase: o.annual_benefit,
      implCost: o.implementation_cost,
      opCost: o.operating_cost,
      paybackMonths: o.payback_months,
      npv3yr: o.npv_3yr,
      calcMethod: o.calc_method,
      confidence: o.confidence,
    },
  };
}

export function getRisks(brief: DecisionBrief): RiskVM[] {
  return getContentRows(brief).risks.map((r) => ({
    riskId: r.risk_id,
    name: r.name,
    category: r.category,
    severity: r.severity,
    likelihood: r.likelihood,
    description: r.description,
    mitigation: r.mitigation,
    owner: r.owner,
  }));
}

export function getMilestones(brief: DecisionBrief): MilestoneVM[] {
  return getContentRows(brief).milestones.map((m) => ({
    label: m.label,
    months: m.months_from_start,
    value: m.value_note,
    dependsOn: m.depends_on,
  }));
}

export function getRequirements(brief: DecisionBrief): RequirementVM[] {
  return getContentRows(brief).requirements.map((q) => ({
    type: q.req_type,
    name: q.name,
    details: q.details,
  }));
}

export function getAlignment(brief: DecisionBrief): AlignmentVM[] {
  return getContentRows(brief).alignment.map((a) => ({
    priority: a.priority,
    score: a.score,
    narrative: a.narrative,
    method: a.method_note,
  }));
}

export { DIM_META, SCORES_AS_OF, SCORING_RUN };
